import React, {
    useMemo,
    useLayoutEffect,
    useEffect,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import {Icon, CBCButton, Loader} from 'shared/helpers';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {DispatchMethod, useJobForm, useJobFormSubmitHandler} from 'hooks';
import {FileUpload} from 'components';
import {Formik, FormikErrors, FormikProps} from 'formik';
import {useParams} from 'react-router-dom';
import {Dropdown} from 'shared/components/Forms/Dropdown';
import {FormControl} from 'shared/components/Forms/FormControl';
import {cloneDeep, isEmpty} from 'lodash';
import {depotListState, selectedDepotState} from 'store/customer/depotSlice';
import {Depot} from 'components/manufacturer/DepotSettings/depotApi';
import {shallowEqual} from 'react-redux';
import {useAppSelector} from 'store/customer';
import {ErrorHandler} from 'shared/components/ErrorHandler';
import {CancelButton} from 'shared/components/CancelButton';
import {DeliveryAddressList} from 'components/customer/Job/DeliveryAddressList';
import DispatchMethodCard from './components/DispatchMethodCard';
import PickupLocation from './components/PickupLocation';
import DispatchNote from './components/DispatchNote';
import {useTabletSize} from 'shared/helpers/DeviceSize';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import {mobile} from 'shared/Breakpoints';
import {DateTime} from '@cabinetsbycomputer/datetime';
import {PartialJob} from 'shared/types/PartialJob';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {DatePickerHeader} from 'shared/components/DatePickerHeader';
import {StyledPicker} from 'components/customer/Job/components/RequestedDeliveryDatePicker';
import CollapseContainer from './components/CollapseContainer';
import {Flex} from 'shared/components/StyledComponents';
import PackagingOptionsSection from './PackagingOptionsSection';

import './Job.scss';
import 'react-datepicker/dist/react-datepicker.css';

export {StyledPicker};

const ButtonContainer = styled.div<{$isHomePage: boolean}>`
    position: absolute;
    padding: 16px 32px;
    box-shadow: 0 0 6px -2px gray;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    button {
        max-width: 400px;
    }

    ${({$isHomePage}) => {
        if ($isHomePage) {
            return `
                z-index: -10;
            `;
        }
    }}
`;

const ErrorHandlerLocal = styled(ErrorHandler)`
    width: 100% !important;
`;

const CollapseContainerLocal = styled(CollapseContainer)`
    margin-left: 0 !important;
    margin-right: 0 !important;

    && {
        border: 1px dashed #d1d1d1;
        margin-top: 15px;
    }

    strong {
        color: gray;
    }
`;

interface JobProps {
    isHomePage?: boolean;
    noNavigate?: boolean;
    onSaved?: () => void;
    isNew?: boolean;
}

export interface JobHandle {
    submit: () => Promise<boolean>;
}

export const Job = forwardRef<JobHandle, JobProps>(function Job(
    {isHomePage = false, noNavigate = false, onSaved, isNew = false},
    ref
) {
    const {jobId: routeJobId} = useParams();
    const jobId = isNew ? undefined : routeJobId;
    const {
        states,
        dispatchMethods,
        allowFileUpload,
        initialValues,
        JobSchema,
        loading,
        updateAttachmentsCount,
        selectDepot,
        allowDeliveryDateRequest,
        minimumLeadTime = 0,
        hideDeliveryPreferences,
    } = useJobForm(jobId);
    const {room, job} = useJobContext() as PartialJob;

    const {jobFormSubmitHandler, loadingSave, dialog} = useJobFormSubmitHandler(
        jobId,
        room?.id,
        noNavigate,
        {preservePackagingOptions: !!hideDeliveryPreferences}
    );
    const formikRef = useRef<FormikProps<typeof initialValues>>(null);

    const handleSubmit = useCallback(
        async (values: typeof initialValues) => {
            const saved = await jobFormSubmitHandler(values);

            if (saved) onSaved?.();
        },
        [jobFormSubmitHandler, onSaved]
    );

    useImperativeHandle(
        ref,
        () => ({
            submit: async () => {
                const formik = formikRef.current;

                if (!formik) return false;

                const errors = await formik.validateForm();
                await formik.submitForm();

                return isEmpty(errors);
            },
        }),
        []
    );
    const {userProfile, setIsMobileMenuVisible} = useAppContext();
    const depotList = useAppSelector(depotListState, shallowEqual);
    const isTabletSize = useTabletSize();

    const depots = useMemo(() => {
        if (typeof depotList === 'undefined') {
            return [];
        }

        return (
            depotList &&
            depotList.map((depot: Depot) => ({
                id: depot.id,
                name: depot.depot_name + ` (${depot.depot_address})`,
            }))
        );
    }, [depotList]);

    const formSpan = useMemo(() => {
        if (isHomePage) {
            return {
                label: 12,
                value: 12,
            };
        }

        const labelSpan = isTabletSize ? 4 : 3;

        return {
            label: labelSpan,
            value: 12 - labelSpan,
        };
    }, [isTabletSize, isHomePage]);

    const mainColSpan = useMemo(() => {
        if (isHomePage) return {span: 12};

        return isTabletSize ? {span: 10, offset: 1} : {span: 8, offset: 2};
    }, [isTabletSize, isHomePage]);

    const depotSelected = useAppSelector(selectedDepotState, shallowEqual);

    useLayoutEffect(() => {
        if (setIsMobileMenuVisible) {
            setIsMobileMenuVisible(false);

            return () => {
                setIsMobileMenuVisible(true);
            };
        }
    }, []);

    const handleDateChange = useCallback(
        (
            setFieldValue: (
                field: string,
                value: boolean | number | string,
                shouldValidate?: boolean
            ) => Promise<void | FormikErrors<typeof initialValues>>
        ) =>
            (date: Date | null) => {
                if (date) {
                    void setFieldValue(
                        'requestedDeliveryDate',
                        DateTime.fromDate(date).format().toString()
                    );
                }
            },
        []
    );

    const preventDefault = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => event.preventDefault(),
        []
    );

    const showFileUpload = allowFileUpload;

    return (
        <>
            <Loader loader={loading || loadingSave} />
            <Row
                className="job-form"
                style={{
                    marginTop: '-5px',
                    padding: '5px 5px 10px 10px',
                }}>
                <Col md={mainColSpan}>
                    {!isHomePage ? (
                        <h1>
                            <Icon iconName="Headers-New-Job.svg" />
                            <label>Job Details</label>
                        </h1>
                    ) : null}

                    <Formik
                        innerRef={formikRef}
                        enableReinitialize={true}
                        initialValues={initialValues}
                        validationSchema={JobSchema}
                        onSubmit={handleSubmit}>
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            setFieldValue,
                            handleSubmit,
                            setFieldTouched,
                            submitCount,
                        }: FormikProps<typeof initialValues>) => {
                            const prevSubmitCount = useRef(submitCount);
                            const errorRef = useRef<HTMLDivElement>(null);
                            const [formFieldErrors, fileUploadErrors] =
                                useMemo(() => {
                                    const formFieldErrors = cloneDeep(errors);
                                    const fileUploadErrors: {
                                        [key: string]:
                                            | FormikErrors<{
                                                  files?: File[];
                                              }>
                                            | undefined;
                                    } = {};

                                    if (
                                        formFieldErrors.hasOwnProperty('files')
                                    ) {
                                        fileUploadErrors['files'] =
                                            formFieldErrors['files'] as
                                                | FormikErrors<{
                                                      files?: File[];
                                                  }>
                                                | undefined;

                                        delete formFieldErrors.files;
                                    }

                                    return [formFieldErrors, fileUploadErrors];
                                }, [errors]);

                            useEffect(() => {
                                if (
                                    Number(
                                        (values as {dispatch: number}).dispatch
                                    ) == DispatchMethod.PICKUP &&
                                    typeof jobId === 'undefined'
                                ) {
                                    void setFieldValue('street', '');
                                    void setFieldValue('postcode', '');
                                    void setFieldValue('state', '');
                                    void setFieldValue('suburb', '');
                                    void setFieldValue('city', '');
                                }
                            }, [
                                (values as {dispatch: number}).dispatch,
                                jobId,
                            ]);

                            useEffect(() => {
                                // this effect will only be triggered whenever form is submitted and validated with errors
                                if (
                                    submitCount > prevSubmitCount.current &&
                                    !isEmpty(errors) &&
                                    !isEmpty(touched)
                                ) {
                                    prevSubmitCount.current = submitCount;
                                    errorRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'end',
                                    });
                                }
                            }, [submitCount, errors, touched]);

                            const handleSelectDepot = useCallback(
                                (_name: string, value: unknown) => {
                                    selectDepot(setFieldValue, value);
                                },
                                [selectDepot]
                            );

                            const onJobNameChange = useCallback(
                                (event: React.ChangeEvent) => {
                                    if (!touched.jobName) {
                                        void setFieldTouched(
                                            'jobName',
                                            true,
                                            true
                                        );
                                    }
                                    handleChange(event);
                                },
                                [touched]
                            );

                            return (
                                <>
                                    <Row
                                        className="jobFormErrors"
                                        ref={errorRef}>
                                        <Col>
                                            <ErrorHandlerLocal
                                                errors={formFieldErrors}
                                                touched={touched}
                                            />
                                        </Col>
                                    </Row>

                                    <FormLocal
                                        id="cbc-job-form"
                                        noValidate
                                        onSubmit={handleSubmit}
                                        className="cbc-form">
                                        <div
                                            style={{
                                                paddingBottom: isHomePage
                                                    ? 0
                                                    : 76,
                                            }}>
                                            <FormGroupLocal
                                                horizontal={false}
                                                asRow
                                                controlId="jobName_">
                                                <Form.Label
                                                    column
                                                    md={formSpan.label}>
                                                    Job Name:<span>*</span>
                                                </Form.Label>
                                                <Col md={formSpan.value}>
                                                    <Form.Control
                                                        autoFocus
                                                        type="text"
                                                        name="jobName"
                                                        value={values.jobName}
                                                        onChange={
                                                            onJobNameChange
                                                        }
                                                        isInvalid={
                                                            touched.jobName &&
                                                            errors.jobName
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                </Col>
                                            </FormGroupLocal>

                                            <Row>
                                                <Col
                                                    xs={
                                                        isHomePage
                                                            ? {
                                                                  span: 6,
                                                                  order: 2,
                                                              }
                                                            : {span: 12}
                                                    }>
                                                    <FormGroupLocal
                                                        horizontal={!isHomePage}
                                                        asRow
                                                        controlId="jobRefCode_">
                                                        <Form.Label
                                                            column
                                                            md={formSpan.label}>
                                                            Job Reference Code:
                                                        </Form.Label>
                                                        <Col
                                                            md={formSpan.value}>
                                                            <FormControl
                                                                type="text"
                                                                name="jobRefCode"
                                                                placeholder="Optional"
                                                                value={
                                                                    values.jobRefCode
                                                                }
                                                            />
                                                        </Col>
                                                    </FormGroupLocal>
                                                </Col>
                                                <Col>
                                                    <FormGroupLocal
                                                        horizontal={!isHomePage}
                                                        asRow
                                                        controlId="contactNumber_">
                                                        <Form.Label
                                                            column
                                                            md={formSpan.label}>
                                                            Contact Number:
                                                        </Form.Label>
                                                        <Col
                                                            md={formSpan.value}>
                                                            <FormControl
                                                                type="text"
                                                                name="contactNumber"
                                                                placeholder="Optional"
                                                                value={
                                                                    values.contactNumber ??
                                                                    ''
                                                                }
                                                                isInvalid={
                                                                    touched.contactNumber &&
                                                                    errors.contactNumber
                                                                        ? true
                                                                        : false
                                                                }
                                                            />
                                                        </Col>
                                                    </FormGroupLocal>
                                                </Col>
                                            </Row>

                                            <FormGroupLocal
                                                horizontal={!isHomePage}
                                                asRow
                                                controlId="description_">
                                                <Form.Label
                                                    column
                                                    md={formSpan.label}>
                                                    Description:
                                                </Form.Label>
                                                <Col md={formSpan.value}>
                                                    <FormControl
                                                        as="textarea"
                                                        name="description"
                                                        placeholder="Optional"
                                                        value={
                                                            values.description
                                                        }
                                                    />
                                                </Col>
                                            </FormGroupLocal>
                                            {allowDeliveryDateRequest &&
                                            !hideDeliveryPreferences ? (
                                                <FormGroupLocal
                                                    horizontal={!isHomePage}
                                                    asRow
                                                    controlId="requestedDeliveryDate_">
                                                    <Form.Label
                                                        column
                                                        md={formSpan.label}>
                                                        {Number(
                                                            values.dispatch
                                                        ) ==
                                                        DispatchMethod.PICKUP
                                                            ? 'Requested Pickup Date:'
                                                            : 'Requested Delivery Date:'}
                                                        {!!userProfile?.requireDeliveryDateRequest ? (
                                                            <span>*</span>
                                                        ) : null}
                                                    </Form.Label>
                                                    <Col md={formSpan.value}>
                                                        <StyledPicker>
                                                            <DatePicker
                                                                popperPlacement="bottom-start"
                                                                renderCustomHeader={
                                                                    DatePickerHeader
                                                                }
                                                                showIcon
                                                                className={`form-control${
                                                                    touched.requestedDeliveryDate &&
                                                                    errors.requestedDeliveryDate
                                                                        ? ' is-invalid'
                                                                        : ''
                                                                }`}
                                                                wrapperClassName="full-width"
                                                                dateFormat="dd MMM yyyy"
                                                                selected={
                                                                    (values.requestedDeliveryDate &&
                                                                        DateTime.parseCustom(
                                                                            values.requestedDeliveryDate
                                                                        ).get()) ||
                                                                    null
                                                                }
                                                                placeholderText={
                                                                    Number(
                                                                        values.dispatch
                                                                    ) ==
                                                                    DispatchMethod.PICKUP
                                                                        ? 'Select a requested pickup date'
                                                                        : 'Select a requested delivery date'
                                                                }
                                                                minDate={DateTime.now()
                                                                    .add({
                                                                        days: minimumLeadTime,
                                                                    })
                                                                    .get()}
                                                                onChange={handleDateChange(
                                                                    setFieldValue
                                                                )}
                                                                onKeyDown={
                                                                    preventDefault
                                                                }
                                                                toggleCalendarOnIconClick
                                                            />
                                                        </StyledPicker>
                                                    </Col>
                                                </FormGroupLocal>
                                            ) : null}

                                            {!hideDeliveryPreferences &&
                                            userProfile?.depotSelectable &&
                                            userProfile?.isDepotFunctionalityEnabled &&
                                            depots.length ? (
                                                <FormGroupLocal
                                                    horizontal={!isHomePage}
                                                    asRow
                                                    controlId="depotId_">
                                                    <Form.Label
                                                        column
                                                        md={formSpan.label}>
                                                        {
                                                            userProfile.depotIdentification
                                                        }
                                                        :
                                                    </Form.Label>
                                                    <Col md={formSpan.value}>
                                                        <Dropdown
                                                            options={depots}
                                                            name="depotId"
                                                            placeholder={
                                                                'Select ' +
                                                                userProfile.depotIdentification
                                                            }
                                                            selectHandler={
                                                                handleSelectDepot
                                                            }
                                                            value={
                                                                values.depotId
                                                            }
                                                            fullWidth
                                                        />
                                                    </Col>
                                                </FormGroupLocal>
                                            ) : null}

                                            {!hideDeliveryPreferences ? (
                                                <section
                                                    style={{
                                                        marginBottom: '15px',
                                                    }}>
                                                    <strong
                                                        className="primary-colour"
                                                        style={{
                                                            marginBottom:
                                                                '15px',
                                                            display: 'block',
                                                        }}>
                                                        How would you like to
                                                        receive your order?
                                                    </strong>

                                                    <DeliveryLayout>
                                                        <DispatchMethodCards>
                                                            {dispatchMethods
                                                                ? dispatchMethods.map(
                                                                      (
                                                                          method
                                                                      ) => (
                                                                          <DispatchMethodCard
                                                                              key={
                                                                                  method.id
                                                                              }
                                                                              {...{
                                                                                  method,
                                                                                  values,
                                                                                  userProfile,
                                                                                  handleChange,
                                                                              }}
                                                                          />
                                                                      )
                                                                  )
                                                                : null}
                                                        </DispatchMethodCards>
                                                        <DeliveryDetails>
                                                            {Number(
                                                                values.dispatch
                                                            ) !=
                                                                DispatchMethod.PICKUP &&
                                                            userProfile?.manufacturerFreightAvailable ? (
                                                                <DeliveryAddressList
                                                                    hideDeliveryPreferences={
                                                                        !!hideDeliveryPreferences
                                                                    }
                                                                />
                                                            ) : null}

                                                            {Number(
                                                                values.dispatch
                                                            ) ==
                                                            DispatchMethod.PICKUP ? (
                                                                <PickupLocation
                                                                    {...{
                                                                        depotSelected,
                                                                        userProfile,
                                                                        states,
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </DeliveryDetails>
                                                    </DeliveryLayout>
                                                </section>
                                            ) : null}

                                            {Number(values.dispatch) !=
                                                DispatchMethod.PICKUP &&
                                            !hideDeliveryPreferences ? (
                                                <DispatchNote />
                                            ) : null}

                                            {!hideDeliveryPreferences ? (
                                                <PackagingOptionsSection
                                                    jobId={job?.id}
                                                    isEditing={!!jobId}
                                                    enforceRequiredGroups
                                                />
                                            ) : null}

                                            {showFileUpload ? (
                                                <CollapseContainerLocal
                                                    asRow={
                                                        !!hideDeliveryPreferences
                                                    }
                                                    initExpanded={
                                                        !!jobId &&
                                                        !!job?.attachments
                                                            ?.length
                                                    }
                                                    iconName="Cloud-Upload.svg"
                                                    headerText="File Upload (Optional)"
                                                    infoText="Upload any supporting documents (e.g. appliance specs, sketches, or plans) to assist with this job -  or simply proceed without if not required. Accepted types: PDF, DOC, DOCX, XLS, XLSX, XML, JPEG, JPG, PNG, GIF, TXT, CSV. Max size: 5MB."
                                                    collapsibleInfoText={true}>
                                                    <FileUpload
                                                        fileUploadErrors={
                                                            fileUploadErrors
                                                        }
                                                        updateAttachmentsCount={
                                                            updateAttachmentsCount
                                                        }
                                                    />
                                                </CollapseContainerLocal>
                                            ) : null}
                                        </div>
                                        <ButtonContainer
                                            $isHomePage={isHomePage}>
                                            <Flex
                                                className="col-md-8 offset-md-2"
                                                $justifyContent="center"
                                                $gap="16px">
                                                <CancelButton to="/v2" />
                                                <CBCButton
                                                    type="submit"
                                                    iconName="Button-Tick.svg"
                                                    className="job-button button-blue"
                                                    disabled={
                                                        !hideDeliveryPreferences &&
                                                        (!values.packagingOptionsLoaded ||
                                                            !values.packagingOptionsValid)
                                                    }>
                                                    {jobId ? 'Save' : 'Create'}{' '}
                                                    Job
                                                </CBCButton>
                                            </Flex>
                                        </ButtonContainer>
                                    </FormLocal>
                                </>
                            );
                        }}
                    </Formik>
                </Col>
            </Row>
            {dialog}
        </>
    );
});

const FormLocal = styled(Form)`
    padding: 0;
`;

const FormGroupLocal = styled(FormGroup)`
    .row {
        margin-right: 0;
    }
`;

const DeliveryLayout = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 15px;

    ${mobile`
        flex-direction: column;
        align-items: stretch;
    `}
`;

const DispatchMethodCards = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 10px;
    width: 140px;

    > div {
        flex: 0 0 auto;
        width: 100%;
        max-width: 100%;
        padding: 0;
    }

    ${mobile`
        flex-direction: row;
        width: 100%;

        > div {
            flex: 0 0 calc(50% - 5px);
        }
    `}
`;

const DeliveryDetails = styled.div`
    flex: 1;
    min-width: 0;

    > div {
        margin-top: 0;
    }
`;
